import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { postUserSignupApi } from '@flipster/apis/v1/user';
import { PostUserSignupParams } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import { supportedLocaleToLanguageTag } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { useCookie } from '@flipster/react/misc/browser/cookie';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { vstack } from '@flipster/styled-system/patterns';
import { FormItem, PasswordInput, SessionInput } from '@flipster/ui';
import { checkAxiosError } from '@flipster/utils/axios';
import { CookieKeys } from '@flipster/utils/cookie';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment, { SessionEvent } from '@flipster/utils/segment';

import { PolicyAgreementCheckbox } from '@web/app/[locale]/(web)/(session)/_policy/PolicyAgreementCheckbox';
import { PasswordStatus } from '@web/components/PasswordStatus';
import { captchaAtom, useCaptcha } from '@web/hooks/useCaptcha';
import { EmailSignUpSchema, checkPasswordValidStatus, emailSignUpValidator } from '@web/utils/session-validator';

import { restrictedCountryBunja } from '../_bunja/restricted-country-state';
import { formItem, h1, header, wrapper } from '../_style';

interface Props {
  setVerifyEmail: (email: string) => void;
  allowRestricted?: boolean;
}

const EmailSignUpForm = ({ setVerifyEmail, allowRestricted }: Props) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const locale = useSupportedLocale();
  const [fbc] = useCookie('fbc');
  const [referralType] = useCookie(CookieKeys.ReferralType);
  const [referralCode, , removeReferralCode] = useCookie(CookieKeys.ReferralCode);

  const { selectedCountryAtom } = useBunja(restrictedCountryBunja);
  const selectedCountry = useAtomValue(selectedCountryAtom);
  const methods = useForm<EmailSignUpSchema>({
    mode: 'onTouched',
    resolver: yupResolver(emailSignUpValidator),
    defaultValues: {
      geetestCaptchaResponse: undefined,
    },
  });

  const [inputType, setInputType] = useState<'password' | 'text'>('password');
  const captcha = useAtomValue(captchaAtom);

  const {
    watch,
    setValue,
    setError,
    register,
    trigger,
    handleSubmit,
    formState: { dirtyFields, touchedFields, errors, isValid },
  } = methods;

  const [isLoading, submit] = useSendApi(async (formValues: EmailSignUpSchema) => {
    try {
      segment.track(SessionEvent.SignUpClick, {
        referral_type: referralType ?? null,
        referral_code: formValues.referralCode ?? null,
        fbc,
      });

      const params: PostUserSignupParams = {
        email: formValues.email,
        password: formValues.password,
        passwordConfirmation: formValues.passwordConfirmation,
        referralCode: formValues.referralCode,
        lang: supportedLocaleToLanguageTag[locale],
        restricted: allowRestricted,
        marketingConsent: formValues.marketingConsent,
        country: selectedCountry?.iso2,
      };

      if (watch('geetestCaptchaResponse'))
        params.geetestCaptchaResponse = formValues.geetestCaptchaResponse || undefined;

      const { data } = await postUserSignupApi(params);
      if (data?.userId) segment.track(SessionEvent.SignUpComplete, { user_id: data.userId.toString() });

      removeReferralCode();
      setVerifyEmail(formValues.email);
    } catch (err) {
      if (!checkAxiosError(err)) return;
      const errorCode = getErrorCode(err);
      const errorMessage = getErrorMessage(err);
      switch (errorCode) {
        case 'api.recaptchaResponseTokenRequired':
        case 'api.recaptchaVerification.timeoutOrDuplicate':
        case 'api.captchaResponseRequired':
        case 'api.geetestCaptchaValidation.duplicate':
          setValue('geetestCaptchaResponse', undefined);
          captcha?.showCaptcha();
          break;
        case 'api.user.create.alreadyTaken': // 이메일 중복
          setError('email', { type: 'server', message: errorMessage }, { shouldFocus: true });
          break;
        case 'api.user.create.wrongPasswordFormat': // 비밀번호 잘못된 포맷 - 중간 공백
          setError('password', { type: 'server', message: errorMessage }, { shouldFocus: true });
          break;
        case 'api.user.create.deactivatedReferralCode':
        case 'api.user.create.invalidReferralCode':
          setError('referralCode', { type: 'server', message: errorMessage }, { shouldFocus: true });
          break;
        default:
          snackbar.error({ message: errorMessage });
      }
    }
  });

  const onChangePassword = async () => {
    if (dirtyFields.passwordConfirmation) await trigger('passwordConfirmation');
  };

  useCaptcha({
    submitFunc: handleSubmit(submit),
    setGeetestCaptchaResponse: (value) => setValue('geetestCaptchaResponse', value),
  });

  useEffect(() => {
    const email = searchParams.get('email');
    if (email) {
      setValue('email', email, { shouldDirty: true, shouldValidate: true });
    }
    if (referralCode) {
      setValue('referralCode', referralCode);
    }
  }, [referralCode, searchParams, setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)}>
        <div className={wrapper}>
          <header className={header}>
            <h1 className={h1}>
              <FormattedMessage id='Autogen_common.r251126.s_Create_account' />
            </h1>
          </header>
          <FormItem
            name='email'
            label={t('Autogen_common.r251126.s_E_mail')}
            error={errors.email?.type === 'server' ? errors.email.message : undefined}
            className={formItem}
          >
            <SessionInput
              type='email'
              placeholder={t('Autogen_common.r251126.s_Email')}
              isValid={touchedFields.email && !errors.email?.message}
              withValidIcon
            />
          </FormItem>
          <FormItem label={t('Autogen_common.r251126.s_Password')} withoutExplain>
            <PasswordInput
              {...register('password', { onChange: onChangePassword })}
              onChangeType={setInputType}
              placeholder={t('Autogen_common.r251126.s_Password')}
            />
          </FormItem>
          <PasswordStatus isError={!!errors.password?.message} {...checkPasswordValidStatus(watch('password'))} />
          <FormItem
            name='passwordConfirmation'
            error={
              errors.passwordConfirmation?.message && (
                <FormattedMessage id='Autogen_common.r251126.s_Password_Not_Match' />
              )
            }
            className={formItem}
          >
            <SessionInput type={inputType} placeholder={t('Autogen_common.r251126.s_Confirm_Password')} />
          </FormItem>
          <FormItem
            name='referralCode'
            label={t('Autogen_common.r251126.s_Referral_Code_Optional_paren')}
            error={errors.referralCode?.type === 'server' ? errors.referralCode.message : undefined}
            className={formItem}
          >
            <SessionInput placeholder={t('Autogen_common.r251126.s_Referral_Code')} />
          </FormItem>
          <div className={vstack({ gap: 16, alignItems: 'flex-start', mb: 16 })}>
            <Checkbox {...register('marketingConsent')} css={{ textStyle: 'Text/L/Regular', color: 'inverse.white' }}>
              <FormattedMessage id='Autogen_common.r251126.s_I_d_like_to_hear_about_USDT_rewards_events_and_new_listing_promotions' />
            </Checkbox>
            <PolicyAgreementCheckbox
              {...register('agreement')}
              agreementType='REGISTER_TNC'
              css={{ textStyle: 'Text/L/Regular', color: 'inverse.white' }}
            />
          </div>

          <Button
            isLoading={isLoading}
            disabled={!isValid}
            size='large'
            htmlType='submit'
            label={t('Autogen_common.r251126.s_Sign_Up')}
            css={{ w: '100%', mt: 8 }}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default EmailSignUpForm;
